import React, { forwardRef, useId } from "react";
import { cn } from "@/lib/utils";

/**
 * Accessible switch. Space toggles. role="switch" + aria-checked.
 * Controlled: pass `checked` + `onCheckedChange`.
 */
export const Switch = forwardRef(function Switch(
  {
    checked = false,
    onCheckedChange,
    disabled = false,
    onDark = false,
    label,
    className,
    id: idProp,
    "data-testid": testId,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const id = idProp ?? `switch-${autoId.replace(/[^a-zA-Z0-9-]/g, "")}`;
  const toggle = () => {
    if (!disabled) onCheckedChange?.(!checked);
  };

  const trackOn = "bg-frac-purple";
  const trackOnDark = "bg-frac-violet";
  const trackOff = "bg-[#B8BDC7]";
  const trackOffDark = "bg-hairline";

  return (
    <span className={cn("inline-flex items-center gap-3", className)}>
      <button
        ref={ref}
        id={id}
        role="switch"
        type="button"
        aria-checked={checked}
        disabled={disabled}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            toggle();
          }
        }}
        data-testid={testId}
        className={cn(
          "relative inline-flex h-[22px] w-10 shrink-0 items-center rounded-full transition-colors duration-base ease-out",
          "disabled:opacity-45 disabled:cursor-not-allowed",
          checked
            ? onDark
              ? trackOnDark
              : trackOn
            : onDark
              ? trackOffDark
              : trackOff,
        )}
        {...rest}
      >
        <span
          aria-hidden
          className={cn(
            "block h-[18px] w-[18px] rounded-full bg-white shadow-elev-sm transition-transform duration-base ease-out",
            checked ? "translate-x-[20px]" : "translate-x-[2px]",
          )}
        />
      </button>
      {label && (
        <label
          htmlFor={id}
          onClick={toggle}
          className={cn(
            "cursor-pointer text-[14px] font-medium",
            onDark ? "text-ink-primary" : "text-frac-purple",
          )}
        >
          {label}
        </label>
      )}
    </span>
  );
});

export default Switch;
